<template>
    <div class="category-items-tab">
        <div class="mb-3">
            <app-form-select2-input
                ref="itemSelect"
                v-model="selectedItemId"
                :label="`Agregar ${categoryName}`"
                :options="itemOptions"
                placeholder="Seleccione un item para agregar"
                @input="onItemSelected"
            />
        </div>

        <div v-if="categorySlug === 'labor'" class="mb-3 p-3 border rounded bg-light">
            <h6 class="mb-2">Agregar Mano de Obra Manual</h6>
            <div class="form-row">
                <div class="col-md-5 mb-2">
                    <app-form-text-input
                        v-model="manualDescription"
                        label="Descripción de la mano de obra"
                    />
                </div>
                <div class="col-md-2 mb-2">
                    <app-form-text-input
                        v-model="manualQuantity"
                        label="Cantidad"
                        type="number"
                        min="1"
                    />
                </div>
                <div class="col-md-3 mb-2">
                    <app-currency-input
                        v-model="manualAmount"
                        label="Valor por Unidad"
                    />
                </div>
                <div class="col-md-2 mb-2 d-flex align-items-end">
                    <button
                        type="button"
                        class="btn btn-primary btn-block"
                        @click="addManualLabor"
                    >
                        <i class="fa fa-plus"></i> Agregar
                    </button>
                </div>
            </div>
        </div>

        <!-- Lista de items agregados -->
        <div v-if="selectedItems.length > 0" class="items-list">
            <div
                v-for="(line, index) in selectedItems"
                :key="line._uniqueId || `${categorySlug}-${index}-${line.item_id || 'manual'}-${line.is_labor_of_item ? 'labor' : ''}-${line.isAccesoriesLine ? 'acc' : ''}-${line.source_item_id || ''}`"
                class="item-row mb-3 p-3 border rounded"
            >
                <div class="row align-items-center">
                    <div class="col-md-6">
                        <div class="d-flex align-items-start">
                            <div v-if="line.item && line.item.image" class="item-image-container mr-3">
                                <img 
                                    :src="line.item.image" 
                                    :alt="line.item.name"
                                    class="item-thumbnail"
                                    @click="showImageModal(line.item.image)"
                                />
                            </div>
                            <div class="flex-grow-1">
                                <h6>
                                    <span v-if="line.item">{{ line.item.name }} - {{ line.item.reference }}</span>
                                    <span v-else-if="line.is_labor_of_item && line.source_item">
                                        Mano de Obra de: {{ line.source_item.name }} - {{ line.source_item.reference }}
                                    </span>
                                    <span v-else-if="line.source_item && !line.is_labor_of_item">
                                        Accesorios de: {{ line.source_item.name }} - {{ line.source_item.reference }}
                                    </span>
                                    <span v-else>{{ line.item_manual || 'Item' }}</span>
                                </h6>
                                <p class="text-muted small mb-1" v-if="line.isLaborLine">
                                    <strong>Precio Unitario Mano de Obra:</strong> {{ formatCurrency(line.unit_price, 'COP') }}
                                </p>
                                <p class="text-muted small mb-1" v-else-if="line.isAccesoriesLine">
                                    <strong>Precio Unitario Accesorios:</strong> {{ formatCurrency(line.unit_price, 'COP') }}
                                </p>
                                <p class="text-muted small mb-1" v-else-if="line.item">
                                    <strong>Precio Unitario:</strong> {{ formatCurrency(line.unit_price, line.currency) }}
                                </p>
                                <p class="text-muted small mb-1" v-else-if="line.item_manual">
                                    <strong>Precio Unitario:</strong> {{ formatCurrency(line.unit_price, line.currency || 'COP') }}
                                </p>
                                <p class="text-muted small" v-if="line.item && line.item.description && !line.isLaborLine && !line.isAccesoriesLine">
                                    {{ line.item.description }}
                                </p>
                                <p class="text-muted small" v-if="line.is_labor_of_item && line.source_item && line.source_item.description">
                                    {{ line.source_item.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div v-if="line.isLaborLine || line.isAccesoriesLine">
                            <label>Cantidad (sincronizada con item original)</label>
                            <input
                                type="number"
                                class="form-control"
                                :value="line.quantity || line.days || ''"
                                min="1"
                                disabled
                                placeholder="Sincronizado automáticamente"
                                readonly
                            />
                        </div>
                        <div v-else-if="line.item || line.item_manual">
                            <label>Cantidad</label>
                            <input
                                type="number"
                                class="form-control"
                                :value="line.quantity || ''"
                                min="1"
                                @input="updateItem(index, $event.target.value)"
                                @blur="handleQuantityBlur(index, line)"
                                placeholder="Ingrese cantidad"
                            />
                        </div>
                    </div>
                    <div class="col-md-2 text-right">
                        <button
                            class="btn btn-sm btn-danger"
                            @click="removeItem(index)"
                            title="Eliminar"
                        >
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col-md-12">
                        <div class="item-calculations">
                            <small class="text-muted">
                                Subtotal: {{ formatCurrency(line.subtotal || 0) }} | 
                                Con margen de ganancia: {{ formatCurrency(line.subtotal_after_multiplier || 0) }}
                                <span v-if="line.isLaborLine && line.labor_cost && parseFloat(line.labor_cost) > 0">
                                    | Mano de Obra: {{ formatCurrency(line.labor_cost || 0) }}
                                </span>
                                <span v-if="line.isAccesoriesLine && line.accesories_cost && parseFloat(line.accesories_cost) > 0">
                                    | Accesorios: {{ formatCurrency(line.accesories_cost || 0) }}
                                </span>
                                | <strong>Total COP: {{ formatCurrency(line.subtotal_cop || 0) }}</strong>
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="text-center text-muted p-4">
            <p>No hay items agregados en esta categoría</p>
        </div>

        <!-- Subtotal de la categoría -->
        <div class="category-subtotal mt-3 p-2 bg-secondary text-white rounded">
            <div class="row">
                <div class="col-md-6">
                    <strong>Subtotal {{ categoryName }}:</strong>
                </div>
                <div class="col-md-6 text-right">
                    <strong>{{ categorySubtotal | currency }}</strong>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FormSelect2Input from '../../../UI/Controls/Forms/FormSelect2Input.vue';
import FormTextInput from '../../../UI/Controls/Forms/FormTextInput.vue';
import CurrencyInput from '../../../UI/Controls/Forms/CurrencyInput.vue';

export default {
    components: {
        appFormSelect2Input: FormSelect2Input,
        appFormTextInput: FormTextInput,
        appCurrencyInput: CurrencyInput
    },
    props: {
        categorySlug: {
            type: String,
            required: true
        },
        categoryName: {
            type: String,
            required: true
        },
        items: {
            type: Array,
            default: () => []
        },
        selectedItems: {
            type: Array,
            default: () => []
        },
        multiplier: {
            type: [Number, String],
            default: 1
        }
    },
    data() {
        return {
            selectedItemId: null,
            manualDescription: '',
            manualQuantity: '1',
            manualAmount: null
        };
    },
    computed: {
        itemOptions() {
            const selectedItemIds = this.selectedItems
                .filter(line => line.item_id && !line.is_labor_of_item && !line.isAccesoriesLine)
                .map(line => line.item_id);
            
            return this.items
                .filter(item => !selectedItemIds.includes(item.id))
                .map(item => ({
                    id: item.id,
                    name: `${item.name} - ${item.reference} (${this.formatCurrency(item.unit_price, item.currency)})`
                }));
        },
        categorySubtotal() {
            let total = 0;
            this.selectedItems.forEach(line => {
                if(line.subtotal_after_multiplier !== undefined)
                    total += parseFloat(line.subtotal_after_multiplier) || 0;
                else    
                    total += parseFloat(line.subtotal_cop) || 0;
            });
            return total;
        }
    },
    watch: {
        multiplier: {
            immediate: false,
            handler(newVal) {
                this.selectedItems.forEach((line, index) => {
                    line.multiplier_used = parseFloat(newVal) || 1;
                    if (line.quantity !== null && line.quantity !== undefined) {
                        this.updateItem(index, line.quantity);
                    }
                });
            }
        }
    },
    methods: {
        onItemSelected(itemId) {
            if (!itemId) return;

            const item = this.items.find(i => i.id === itemId);
            if (item) {
                this.$emit('add-item', {
                    categorySlug: this.categorySlug,
                    item: item
                });
                
                this.selectedItemId = null;
                
                this.$nextTick(() => {
                    if (this.$refs.itemSelect) {
                        const vSelectComponent = this.$refs.itemSelect.$children.find(
                            child => child.$options.name === 'v-select' || child.$options._componentTag === 'v-select'
                        );
                        if (vSelectComponent && typeof vSelectComponent.close === 'function') {
                            vSelectComponent.close();
                        }
                    }
                });
            }
        },
        removeItem(index) {
            this.$emit('remove-item', {
                categorySlug: this.categorySlug,
                index: index
            });
        },
        updateItem(index, value) {
            const line = this.selectedItems[index];
            if (line) {
                if (value === '' || value === null || value === undefined) {
                    this.$set(line, 'quantity', null);
                    this.$set(line, 'days', null);

                    this.$set(line, 'subtotal', 0);
                    this.$set(line, 'subtotal_after_multiplier', 0);
                    this.$set(line, 'labor_cost', 0);
                    this.$set(line, 'subtotal_cop', 0);
                    
                    this.$emit('update-item', {
                        index: index,
                        categorySlug: this.categorySlug,
                        line: line,
                        quantity: null,
                        item: line.item,
                        hasLabor: line.item && line.item.labor && parseFloat(line.item.labor) > 0,
                    });
                    return;
                }
                
                const numValue = parseInt(value);
                if (!isNaN(numValue) && numValue > 0) {
                    this.$set(line, 'quantity', numValue);
                    this.$set(line, 'days', null); 
                } else {
                    this.$set(line, 'quantity', null);
                    this.$set(line, 'days', null);
                }
                    
                const quantityValue = line.quantity || 0;
                const unitPrice = parseFloat(line.unit_price) || 0;
                const mult = parseFloat(line.multiplier_used) || parseFloat(this.multiplier) || 1;

                if (line.isLaborLine || line.isAccesoriesLine) {
                    line.subtotal = quantityValue * unitPrice; 
                    line.subtotal_after_multiplier = line.subtotal / mult;
                    line.labor_cost = 0; 
                    line.subtotal_cop = line.subtotal_after_multiplier;
                } else {
                    line.subtotal = quantityValue * unitPrice;
                    line.subtotal_after_multiplier = line.subtotal / mult;
                    line.labor_cost = 0; 
                    line.subtotal_cop = line.subtotal_after_multiplier;
                }

                this.$emit('update-item', {
                    index: index,
                    categorySlug: this.categorySlug,
                    line: line,
                    quantity: line.quantity,
                    item: line.item,
                    hasLabor: line.item && line.item.labor && parseFloat(line.item.labor) > 0,
                });
            }
        },
        addManualLabor() {
            const quantity = parseInt(this.manualQuantity) || 0;
            if (!this.manualDescription || !this.manualAmount || this.manualAmount <= 0 || !quantity || quantity <= 0) {
                toastr.warning('Por favor complete todos los campos correctamente');
                return;
            }

            this.$emit('add-manual-labor-item', {
                description: this.manualDescription,
                quantity: quantity,
                unitPrice: this.manualAmount
            });

            this.manualDescription = '';
            this.manualQuantity = '1';
            this.manualAmount = null;
        },
        getQuantityValue(line) {
            if (!line) return '';

            if (line.quantity !== null && line.quantity !== undefined && line.quantity !== '') {
                const qty = typeof line.quantity === 'number' ? line.quantity : parseInt(String(line.quantity), 10);
                if (!isNaN(qty) && qty >= 0) {
                    return String(qty);
                }
            }

            return '';
        },
        handleQuantityBlur(index, line) {
            if ((line.quantity === null || line.quantity === undefined || line.quantity === '') && 
                (line.days === null || line.days === undefined || line.days === '')) {
                line.quantity = 1;
                line.days = null;
                this.updateItem(index, '1');
            }
        },
        showImageModal(imageUrl) {
            window.open(imageUrl, '_blank');
        },
        formatCurrency(value, currency = 'COP') {
            if (!value && value !== 0) return '$ 0,00';
            return new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        }
    },
    filters: {
        currency(value) {
            if (!value && value !== 0) return '$ 0,00';
            return new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        }
    }
};
</script>

<style scoped>
.category-items-tab {
    padding: 10px;
}

.item-row {
    background: #f8f9fa;
}

.item-calculations {
    border-top: 1px solid #dee2e6;
    padding-top: 10px;
    margin-top: 10px;
}

.category-subtotal {
    font-size: 1.1em;
}

.item-image-container {
    flex-shrink: 0;
}

.item-thumbnail {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 6px;
    cursor: pointer;
    transition: transform 0.2s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: 1px solid #dee2e6;
}

.item-thumbnail:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}
</style>

